import { generateHTML } from '@tiptap/react';
import AxiosConfig from '../../../config/AxiosConfig';

export const validateEmail = (emailToAdd) => {
  const regex = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
  const email = emailToAdd?.trim();
  const isValid = email?.match(regex);
  return { isValid, email };
};

export const extractInlineImagesFromMessage = (htmlContent) => {
  if (!htmlContent) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const images = doc.getElementsByTagName('img');

  const bodyAttachments = Array.from(images).map((img) => {
    const style = img.getAttribute('style') || '';
    const styleObj = {};

    if (style) {
      style.split(';').forEach((styleRule) => {
        const [property, value] = styleRule.split(':').map((s) => s.trim());
        if (property && value) {
          styleObj[property] = value;
        }
      });
    }

    return {
      src: img.getAttribute('src') || '',
      alt: img.getAttribute('alt') || '',
      style: styleObj,
      width: img.getAttribute('width') || styleObj.width || 'auto',
      height: styleObj.height || 'auto',
      display: styleObj.display || 'inline-block',
      cursor: styleObj.cursor || 'default',
      textAlign: styleObj.textAlign || 'left',
      margin: styleObj.margin || '0',
    };
  });

  return bodyAttachments.filter(
    (att) => !att?.src?.includes('pandora/logo-hoortrade.jpg')
  );
};

export const extractInlineImagesFromEditor = ({ editor }) => {
  const extractImagesFromNode = (node) => {
    if (node.type === 'image') {
      return [
        {
          src: node.attrs.src,
          alt: node.attrs.alt || '',
          title: node.attrs.title || '',
          width: node.attrs.width,
          height: node.attrs.height,
        },
      ];
    }
    if (node.content && Array.isArray(node.content)) {
      return node.content.reduce((acc, childNode) => {
        return [...acc, ...extractImagesFromNode(childNode)];
      }, []);
    }
    return [];
  };

  const images = editor.getJSON().content.reduce((acc, node) => {
    return [...acc, ...extractImagesFromNode(node)];
  }, []);

  return images.filter(
    (image) => !image?.src?.includes('pandora/logo-hoortrade.jpg')
  );
};

export const HANDLE_MESSAGES_REQUEST = {
  sendMessage: async ({
    messageParams,
    isTicketProcessed,
    draftId,
    handleCloseMessageBox,
  }) => {
    const messageData = {
      userId: messageParams.userId,
      ticketId: messageParams.ticketId,
      subject: messageParams.subject,
      content: messageParams.content,
      receivers: messageParams.receivers,
      recipientType: messageParams.recipientType,
      isTicketProcessed: isTicketProcessed,
    };

    try {
      const response = await AxiosConfig.create(
        'post',
        `/messages/${draftId}/send`
      ).call(messageData);
      console.log('response', response);
      handleCloseMessageBox();
    } catch (err) {
      console.error("[sendMessage] Erreur lors de l'envoi du message:", err);
      throw err;
    }
  },

  createNewMessageDraft: async ({
    ticket,
    subject,
    receiverList,
    currentRecipientType,
    mutateTicket,
  }) => {
    try {
      await AxiosConfig.create('post', '/messages/create')
        .call({
          ticketId: ticket?._id,
          ticketRef: ticket?.reference,
          subject: subject,
          content: '',
          receivers: receiverList,
          recipientType: currentRecipientType,
        })
        .exit(() => {
          mutateTicket();
        });
    } catch (err) {
      console.error(
        '[createNewMessageDraft] Erreur lors de la création du brouillon:',
        err
      );
      throw err;
    }
  },

  saveMessage: async ({
    draft,
    profile,
    ticket,
    subject,
    content,
    receiverList,
    recipientType,
    mutateTicket,
    mutate,
    setIsLoadingSave,
  }) => {
    if (!draft?._id) return;
    try {
      await AxiosConfig.create('put', `/messages/${draft?._id}/save`)
        .call({
          userId: profile?._id,
          ticketId: ticket?._id,
          subject: subject,
          content,
          receivers: receiverList,
          recipientType: recipientType,
        })
        .exit(() => {
          mutateTicket();
          mutate();
          setIsLoadingSave(false);
        });
    } catch (err) {
      console.error(
        '[saveMessage] Erreur lors de la sauvegarde du message:',
        err
      );
    }
  },

  createAttachment: async ({
    attachments = [],
    draftId,
    attachmentData,
    mutateTicket,
    mutate,
    setIsLoadingSend,
    setIsLoadingSave,
  }) => {
    const getFileName = (attachment) =>
      attachment?.file?.name || attachment?.name;
    const getFileProperties = (attachment) => ({
      name: getFileName(attachment),
      size: attachment?.file?.size || attachment?.size,
      type: attachment?.file?.type || attachment?.type || attachment?.mimeType,
    });
    const incrementFileName = (fileName, existingNames) => {
      const lastDotIndex = fileName.lastIndexOf('.');
      const nameWithoutExt =
        lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
      const extension =
        lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
      let counter = 1;
      let newName;
      do {
        newName = `${nameWithoutExt}-${counter}${extension}`;
        counter++;
      } while (existingNames.includes(newName));
      return newName;
    };

    const attachementToCreate = {
      _id: attachmentData?._id,
      content: attachmentData?.content,
      name: attachmentData?.name || attachmentData?.file?.name,
      file: {
        type:
          attachmentData.file?.type ||
          attachmentData?.mimeType ||
          attachmentData?.type,
        size: attachmentData.file?.size || attachmentData?.size,
        name: attachmentData.file?.name || attachmentData?.name,
        cid: attachmentData?.cid,
        lastModifiedDate:
          attachmentData.file?.lastModifiedDate ||
          attachmentData?.lastModifiedDate,
      },
      link: attachmentData?.link,
      isAtt: attachmentData?.isAtt || false,
    };

    // Gestion des doublons
    if (!attachmentData?._id) {
      const newFileProps = getFileProperties(attachmentData);
      const attachmentsWithId = attachments.filter((att) => att?._id);
      const existingFileNames = attachmentsWithId
        .map((att) => getFileName(att))
        .filter(Boolean);

      const conflictingAttachments = attachmentsWithId.filter((att) => {
        const existingProps = getFileProperties(att);
        return existingProps.name === newFileProps.name;
      });

      if (conflictingAttachments.length > 0) {
        const exactDuplicate = conflictingAttachments.find((att) => {
          const existingProps = getFileProperties(att);
          return (
            existingProps.name === newFileProps.name &&
            existingProps.size === newFileProps.size &&
            existingProps.type === newFileProps.type
          );
        });

        if (exactDuplicate) {
          mutateTicket();
          return { data: null, error: null, isDuplicateAttachment: true };
        }

        const newFileName = incrementFileName(
          newFileProps.name,
          existingFileNames
        );
        attachementToCreate.name = newFileName;
        attachementToCreate.file.name = newFileName;
      }
    }

    try {
      const responseData = await AxiosConfig.create(
        'post',
        `/messages/${draftId}/attachments`
      )
        .call(attachementToCreate)
        .getResponseData();

      // Mettre à jour le state avec les données de l'API
      console.log('mutateTicket createAttachment');
      mutateTicket();
      mutate();
      setIsLoadingSend(false);
      setIsLoadingSave(false);

      // Retourner les données formatées pour la mise à jour du state
      return {
        data: {
          ...responseData,
          status: 'default', // Mettre à jour le statut après la réponse de l'API
        },
        error: null,
        isDuplicateAttachment: false,
      };
    } catch (err) {
      console.error(
        "❌ [HANDLE_MESSAGES_REQUEST] createAttachment - Erreur lors de l'appel API:",
        err
      );
      setIsLoadingSend(false);
      setIsLoadingSave(false);
      return { data: null, error: err, isDuplicateAttachment: false };
    }
  },

  deleteAttachment: async ({
    draftId,
    attachementToDelete,
    mutateTicket,
    mutate,
    setIsLoadingSend,
    setIsLoadingSave,
  }) => {
    if (!attachementToDelete?._id || attachementToDelete?.status === 'loading')
      return;

    try {
      await AxiosConfig.create(
        'delete',
        `/messages/${draftId}/attachments/${attachementToDelete?._id}`
      )
        .call()
        .exit(() => {
          mutateTicket();
          mutate();
          setIsLoadingSend(false);
          setIsLoadingSave(false);
        });
    } catch (error) {
      console.error(
        '❌ [HANDLE_MESSAGES_REQUEST] deleteAttachment - Erreur:',
        error
      );
      throw error;
    }
  },

  translateText: async ({
    editor,
    extensions,
    languages,
    selectedIndex,
    setIsLoadingTranslation,
  }) => {
    setIsLoadingTranslation(true);
    try {
      await AxiosConfig.create('post', '/translate')
        .call({
          content: editor.getJSON(),
          target: languages[selectedIndex].code2,
        })
        .exit((res) => {
          editor.commands.setContent(generateHTML(res.data?.data, extensions));
          setIsLoadingTranslation(false);
        });
    } catch (err) {
      console.error(
        '[translateText] Erreur lors de la traduction du texte:',
        err
      );
      setIsLoadingTranslation(false);
      throw err;
    }
  },
};

export const EDITOR_UTILS = {
  handlePaste: async ({
    _view,
    event,
    ticket,
    attachmentsSize,
    onFileAdd,
    editor,
    handleEditorBoxMedia,
    setEditorHasChanged,
  }) => {
    console.log('🔄 handlePaste');
    const items = event.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (
        item?.type?.startsWith('image/') &&
        ticket?.contactChannelType === 'MAIL'
      ) {
        const file = item.getAsFile();
        if (file) {
          // Vérifier la taille totale avant d'ajouter
          if (
            attachmentsSize + file.size >
            (Number(import.meta.env.VITE_MAXIMUM_TOTAL_FILES_SIZE) || 20000000)
          ) {
            console.warn('Taille maximale des pièces jointes atteinte.');
            return true;
          }
          const newAttachment = await handleEditorBoxMedia(file);
          if (onFileAdd) {
            await onFileAdd(newAttachment);
          }
          setEditorHasChanged(true);
          return true;
        }
      } else if (item.type === 'text/html') {
        const html = event.clipboardData.getData('text/html');
        if (html) {
          editor.commands.insertContent(html, {
            parseOptions: { preserveWhitespace: false },
          });
          return true;
        }
      } else if (item.type === 'text/plain') {
        const text = event.clipboardData.getData('text/plain');
        if (text) {
          editor.commands.insertContent(text);
          return true;
        }
      }
    }
    return false;
  },

  handleDrop: async ({
    _view,
    event,
    ticket,
    attachmentsSize,
    onFileAdd,
    editor,
    handleEditorBoxMedia,
    setEditorHasChanged,
  }) => {
    event.preventDefault();
    const items = event.dataTransfer?.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (
        item?.type?.startsWith('image/') &&
        ticket?.contactChannelType === 'MAIL'
      ) {
        const file = item.getAsFile();
        if (file) {
          if (
            attachmentsSize + file.size >
            (Number(import.meta.env.VITE_UM_TOTAL_FILES_SIZE) || 20000000)
          ) {
            console.warn('Taille maximale des pièces jointes atteinte.');
            return true;
          }
          const newAttachment = await handleEditorBoxMedia(file);
          if (onFileAdd) {
            await onFileAdd(newAttachment);
          }
          setEditorHasChanged(true);
          return true;
        }
      } else if (item.type === 'text/html') {
        const html = event.dataTransfer.getData('text/html');
        if (html) {
          editor.commands.insertContent(html, {
            parseOptions: { preserveWhitespace: false },
          });
          return true;
        }
      } else if (item.type === 'text/plain') {
        const text = event.dataTransfer.getData('text/plain');
        if (text) {
          editor.commands.insertContent(text);
          return true;
        }
      }
    }
    return false;
  },
};
